import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from 'app/services/apiService';
import { getAllPolicies, setLoading } from './privacySlice';

const initialState = {
  WithoutTemplate: {},
  formattedData: [],
  changes: false,
  privacyPersistedError: null,
  columns: {},
};

export const createWithoutTemplate = createAsyncThunk(
  'withoutTemplate',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      const { data } = await apiService.createPolicyWithoutTemplate(payload);
      dispatch(setLoading(false));
      return data;
    } catch (error) {
      dispatch(setLoading(false));
      return rejectWithValue(error);
    }
  }
);

export const createWithTemplate = createAsyncThunk(
  'withTemplate',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      const { data } = await apiService.createPolicyWithTemplate(payload);
      dispatch(setLoading(false));
      return data;
    } catch (error) {
      dispatch(getAllPolicies());
      dispatch(setLoading(false));
      return rejectWithValue(error);
    }
  }
);

export const getPolicyById = createAsyncThunk(
  'policy',
  async ({ id, readOnly }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      const { data } = await apiService.getPolicyById({ id, readOnly });
      dispatch(setLoading(false));
      return data;
    } catch (error) {
      dispatch(getAllPolicies());
      dispatch(setLoading(false));
      return rejectWithValue(error);
    }
  }
);

export const getActivatedPolicyByDate = createAsyncThunk(
  'policyByDate',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      const { data } = await apiService.getPolicyByDate(payload);
      dispatch(setLoading(false));
      return data;
    } catch (error) {
      dispatch(setLoading(false));
      return rejectWithValue(error);
    }
  }
);

export const dragDropItems = createAsyncThunk('dragDrop', async (payload, { rejectWithValue }) => {
  try {
    const { data } = await apiService.dragDropItems(payload?.id, {
      addedInfoObject: { infoObjectId: payload?.dragId, sortOrder: payload?.index },
    });
    return data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const repositionColumns = createAsyncThunk(
  'reposition',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await apiService.repositionColumns(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const manageProtectionAspects = createAsyncThunk('protectionAspects', async payload => {
  try {
    const response = await apiService.manageProtectionAspects(payload);
    return response;
  } catch (error) {
    console.error(error);
  }
});

export const privacySlice = createSlice({
  name: 'privacy',
  initialState,
  reducers: {
    setWithoutTemplate(state, { payload }) {
      state.WithoutTemplate = payload;
    },
    setWithoutTemp(state, { payload }) {
      state.formattedData = payload;
    },
    setChanges(state, { payload }) {
      state.changes = payload;
    },
    setPrivacyPersistedError(state, { payload }) {
      state.privacyPersistedError = payload;
    },
    setColumns(state, { payload }) {
      state.columns = payload;
    },
  },

  extraReducers(builder) {
    // pending case not required as it only set loading to true which is being handled in thunk
    builder.addCase(createWithoutTemplate.fulfilled, (state, { payload }) => {
      state.WithoutTemplate = payload;
    });
    builder.addCase(createWithoutTemplate.rejected, (state, { payload }) => {
      state.privacyPersistedError = payload.response.data;
    });

    builder.addCase(createWithTemplate.fulfilled, (state, { payload }) => {
      state.WithoutTemplate = payload;
      state.changes = true;
    });
    builder.addCase(createWithTemplate.rejected, (state, { payload }) => {
      state.privacyPersistedError = payload.response.data;
    });

    builder.addCase(getPolicyById.fulfilled, (state, { payload }) => {
      state.WithoutTemplate = payload;
    });
    builder.addCase(getPolicyById.rejected, (state, { payload }) => {
      state.privacyPersistedError = payload.response.data;
    });

    builder.addCase(getActivatedPolicyByDate.fulfilled, (state, { payload }) => {
      state.WithoutTemplate = payload;
    });
    builder.addCase(getActivatedPolicyByDate.rejected, (state, { payload }) => {
      state.privacyPersistedError = payload.response.data;
    });

    builder.addCase(manageProtectionAspects.fulfilled, state => {
      state.changes = true;
    });

    builder.addCase(dragDropItems.fulfilled, state => {
      state.changes = true;
    });
    builder.addCase(dragDropItems.rejected, (state, { payload }) => {
      state.privacyPersistedError = payload.response.data;
    });

    builder.addCase(repositionColumns.fulfilled, state => {
      state.changes = true;
    });
    builder.addCase(repositionColumns.rejected, (state, { payload }) => {
      state.privacyPersistedError = payload.response.data;
    });
  },
});

export const {
  setWithoutTemplate,
  setWithoutTemp,
  setChanges,
  setPrivacyPersistedError,
  setColumns,
} = privacySlice.actions;
export default privacySlice.reducer;
